import clsx from 'clsx'
import { ChangeEvent, useCallback, useMemo } from 'react'

type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> & {
  invalid?: boolean
  shouldDebounce?: boolean
  onChange: (newValue: string) => void
}

export default function ColorPicker({
  className,
  invalid = false,
  onChange,
  value,
  ...rest
}: Props) {
  const onChangeColor = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value

      if (!value) return

      if (!value.startsWith('#')) {
        onChange(`#${value.toUpperCase()}`)

        return
      }

      onChange(value.toUpperCase())
    },
    [onChange]
  )

  const valueFilledWithZero = useMemo(() => {
    if (!value || typeof value !== 'string') return '#000000'

    const hexValue = value.startsWith('#') ? value.slice(1) : value
    const paddedHex = hexValue.padEnd(6, '0')

    return `#${paddedHex}`
  }, [value])

  return (
    <label
      className={clsx(
        'flex cursor-pointer flex-row items-center gap-2 rounded-small border border-text bg-white px-4 py-2 text-regular text-text placeholder:text-text-light focus-within:outline-solid focus-within:outline-primary',
        invalid && 'border-red-500',
        className
      )}
    >
      <div className="relative size-5 overflow-hidden rounded-xxsmall">
        <input
          {...rest}
          type="color"
          aria-invalid={invalid}
          onChange={onChangeColor}
          value={valueFilledWithZero}
          className="absolute inset-1/2 size-10 -translate-x-1/2 -translate-y-1/2 appearance-none border-none bg-transparent p-0"
        />
      </div>
      {value && typeof value === 'string' && (
        <input
          type="text"
          className="w-[70px] outline-0"
          value={value}
          onChange={onChangeColor}
          aria-invalid={invalid}
        />
      )}
      {!value && <span className="text-text-light">{rest.placeholder}</span>}
    </label>
  )
}
